import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  ViewEncapsulation,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HxIcon } from '../icon/icon';
import { HxTooltipDirective, TooltipColor, TooltipPosition } from '../tooltip/tooltip';

/** Text alignment for label content. */
export type LabelJustification = 'left' | 'center' | 'right';

/**
 * HxLabel component for displaying sanitized description labels.
 *
 * Accepts description input and automatically sanitizes it to prevent XSS attacks. Supports content justification
 * (left, center, right) for text alignment.
 *
 * @example
 *   ```html
 *   <hx-label [description]="'Hello World'" contentJustification="center"></hx-label>
 *   ```;
 */
@Component({
  selector: 'hx-label',
  templateUrl: './label.html',
  styleUrls: ['./label.css'],
  standalone: true,
  imports: [HxIcon, HxTooltipDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[attr.id]': 'id()',
  },
})
export class HxLabel {
  /**
   * Tooltip text displayed beside the label.
   */
  readonly tooltip: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * Tooltip position relative to the icon.
   */
  readonly tooltipPosition = input<TooltipPosition>('top');

  /**
   * Tooltip color scheme.
   */
  readonly tooltipColor = input<TooltipColor>('info');

  /**
   * Unique identifier for the label element.
   *
   * @default undefined
   */
  readonly id: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * ID of the form control this label is associated with. Used for accessibility to link the label with its input
   * element.
   *
   * @default undefined
   */
  readonly for: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * Description content to display in the label. The description will be sanitized to prevent XSS attacks.
   *
   * @default ''
   */
  readonly description: InputSignal<string> = input<string>('');

  /**
   * Subtitle content to display below the main description. The subtitle will be sanitized to prevent XSS attacks.
   *
   * @default ''
   */
  readonly subtitle: InputSignal<string> = input<string>('');
  readonly width: InputSignal<string> = input<string>('');
  readonly required: InputSignal<boolean> = input<boolean>(false);

  /** Whether to render the label text in bold. */
  readonly bold: InputSignal<boolean> = input<boolean>(false);

  /**
   * Font weight selection for the label text. Accepts: 'Light', 'Regular', 'Medium', 'Semi Bold', 'Bold', 'Italic',
   * 'Underline'. When set, this takes precedence over the `bold` input.
   *
   * @default 'Semi Bold'
   */
  readonly fontWeight: InputSignal<string | undefined> = input<string | undefined>('Semi Bold');

  /**
   * Font size for the label text in pixels.
   *
   * @default undefined (inherits from CSS)
   */
  readonly fontSize: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * Font weight selection for the subtitle text. Accepts: 'Light', 'Regular', 'Medium', 'Semi Bold', 'Bold', 'Italic',
   * 'Underline'.
   *
   * @default 'Regular'
   */
  readonly subtitleFontWeight: InputSignal<string | undefined> = input<string | undefined>('Regular');

  /**
   * Font size for the subtitle text in pixels.
   *
   * @default '14'
   */
  readonly subtitleFontSize: InputSignal<string | undefined> = input<string | undefined>('14');

  /**
   * Text alignment for label content.
   *
   * @default 'left'
   */
  readonly contentJustification: InputSignal<LabelJustification> = input<LabelJustification>('left');

  /**
   * Color for the label text. Accepts any valid CSS color value (hex, rgb, rgba, named colors, etc.).
   *
   * @default undefined
   */
  readonly color: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * Color for the subtitle text. Accepts any valid CSS color value (hex, rgb, rgba, named colors, etc.).
   *
   * @default undefined (falls back to CSS default gray)
   */
  readonly subtitleColor: InputSignal<string | undefined> = input<string | undefined>(undefined);

  private sanitizer = inject(DomSanitizer);

  /**
   * Returns sanitized HTML content for description.
   *
   * @internal
   */
  protected get sanitizedDescription(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.description()) || '';
  }

  /**
   * Returns sanitized HTML content for subtitle.
   *
   * @internal
   */
  protected get sanitizedSubtitle(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.subtitle()) || '';
  }

  /** Maps font weight selection to CSS properties */
  private mapFontWeight(value: string | undefined): { weight?: string; style?: string; decoration?: string } {
    switch (value) {
      case 'Light':
        return { weight: '300' };
      case 'Regular':
        return { weight: '400' };
      case 'Medium':
        return { weight: '500' };
      case 'Semi Bold':
        return { weight: '600' };
      case 'Bold':
        return { weight: '700' };
      case 'Italic':
        return { style: 'italic' };
      case 'Underline':
        return { decoration: 'underline' };
      default:
        return {};
    }
  }

  /** Computed CSS font-weight for label */
  readonly labelFontWeightStyle = computed(() => this.mapFontWeight(this.fontWeight()).weight);

  /** Computed CSS font-style for label */
  readonly labelFontStyle = computed(() => this.mapFontWeight(this.fontWeight()).style);

  /** Computed CSS text-decoration for label */
  readonly labelTextDecoration = computed(() => this.mapFontWeight(this.fontWeight()).decoration);

  /** Computed CSS font-size for label */
  readonly labelFontSizeStyle = computed(() => {
    const size = this.fontSize();
    return size ? `${size}px` : undefined;
  });

  /** Computed CSS font-weight for subtitle */
  readonly subtitleFontWeightStyle = computed(() => this.mapFontWeight(this.subtitleFontWeight()).weight);

  /** Computed CSS font-style for subtitle */
  readonly subtitleFontStyle = computed(() => this.mapFontWeight(this.subtitleFontWeight()).style);

  /** Computed CSS text-decoration for subtitle */
  readonly subtitleTextDecoration = computed(() => this.mapFontWeight(this.subtitleFontWeight()).decoration);

  /** Computed CSS font-size for subtitle */
  readonly subtitleFontSizeStyle = computed(() => {
    const size = this.subtitleFontSize();
    return size ? `${size}px` : undefined;
  });

  readonly validatedWidth = computed(() => {
    const width = this.width()?.trim() ?? '';

    // Empty width means no explicit value was configured; use the component default silently.
    if (!width) {
      return '100%';
    }

    if (this.isValidWidth(width)) {
      return width;
    }
    console.warn(`Invalid width '${width}' provided to hx-label; using default 100%`);
    return '100%';
  });

  /** Validate width format. Accepts CSS lengths plus common keywords like `auto`. */
  private isValidWidth(width: string): boolean {
    if (!width) {
      return false;
    }

    const normalizedWidth = width.trim().toLowerCase();

    // Common non-length CSS width keywords.
    if (
      ['auto', 'fit-content', 'min-content', 'max-content', 'inherit', 'initial', 'unset'].includes(normalizedWidth)
    ) {
      return true;
    }

    // Accept CSS length values (%, px, rem, em, vw, etc.).
    const validPattern = /^(\d+(\.\d+)?)(px|rem|em|%|vw|vh|vmin|vmax|ch|ex|cm|mm|in|pt|pc)$/;
    return validPattern.test(normalizedWidth);
  }
}
